/* eslint-disable react-hooks/set-state-in-effect -- Effects sync internal payers state with the external `value` prop and auto-recompute the single-payer amount when totalAmount changes; refs are used to break dependency loops. */
import clsx from 'clsx';
import {
  AlertCircleIcon,
  ChevronDownIcon,
  Trash2Icon,
  UserIcon,
  UserPlusIcon,
} from 'lucide-react';

import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { extractMemberData, normalizeDecimalInput } from '@splitfiesta/core';
import { MultiPayerCalculationService } from '@splitfiesta/core/services/expense/MultiPayerCalculationService';
import type { Payer } from '@splitfiesta/core/services/expense/MultiPayerCalculationService';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import {
  formatAmountForCurrency,
  roundToCurrencyPrecision,
} from '@splitfiesta/shared';
import { Button } from '@splitfiesta/ui';

import CurrencyInput, { CurrencyInputRef } from '@/components/ui/CurrencyInput';

import { resolveUserName } from '@/lib/displayName';
import { getInputClasses } from '@/lib/styles/form';

interface MultiPayerSelectProps {
  members: Array<{
    userId: string | { _id: string; name: string };
    name?: string;
    isPlaceholder?: boolean;
    /** Claimant name when this placeholder has an unresolved claim from a member. */
    claimPendingFor?: string;
  }>;
  value: Payer[];
  onChange: (value: Payer[]) => void;
  totalAmount: number;
  currency: string;
  currentUserId?: string;
  disabled?: boolean;
  error?: string;
  /** Shown under the payer only while one person paid; amounts are mid-edit otherwise. */
  saveSpot?: ReactNode;
}

export function MultiPayerSelect({
  members,
  value,
  onChange,
  totalAmount,
  currency,
  currentUserId,
  disabled = false,
  error,
  saveSpot,
}: MultiPayerSelectProps) {
  const { t } = useTranslation();
  const [payers, setPayers] = useState<Payer[]>([]);

  // The placeholder suffix on the option labels comes from `groups`.
  useNamespace('groups');

  // Ref to track if we're in the middle of updating from external value
  const isUpdatingFromValueRef = useRef(false);
  // Ref to track the last totalAmount we processed
  const lastProcessedAmountRef = useRef<number>(0);
  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Refs for amount inputs to enable focus on validation error
  const amountInputRefs = useRef<Map<number, CurrencyInputRef>>(new Map());
  // Refs for payer select dropdowns
  const payerSelectRefs = useRef<Map<number, HTMLSelectElement>>(new Map());

  // Don't filter members - just use them as is
  const validMembers = members || [];

  // Immediate onChange for real-time synchronization with collapsed view
  const debouncedOnChange = useCallback(
    (newPayers: Payer[]) => {
      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // For single payer scenarios, update immediately for better UX
      if (newPayers.length === 1) {
        onChange(newPayers);
        debounceTimerRef.current = null;
      } else {
        // For multiple payers, use minimal debounce
        debounceTimerRef.current = setTimeout(() => {
          onChange(newPayers);
          debounceTimerRef.current = null;
        }, 10);
      }
    },
    [onChange]
  );

  // Initialize state from value - prevent dependency loop
  useEffect(() => {
    if (Array.isArray(value) && value.length > 0) {
      isUpdatingFromValueRef.current = true;
      setPayers(value);
      // Small delay to allow state to settle
      setTimeout(() => {
        isUpdatingFromValueRef.current = false;
      }, 10);
    } else if (payers.length === 0) {
      // Initialize with empty payer if no value provided
      const initialPayers = [{ userId: '', amount: 0 }];
      setPayers(initialPayers);
    }
    // Note: payers.length is intentionally omitted to prevent dependency loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]); // Remove payers.length dependency to prevent loops

  // Auto-update single payer amount when total changes - with debouncing
  useEffect(() => {
    // Use service to check if we should update
    if (
      payers.length > 0 &&
      MultiPayerCalculationService.shouldUpdateSinglePayerAmount(
        payers,
        totalAmount,
        lastProcessedAmountRef.current,
        isUpdatingFromValueRef.current
      )
    ) {
      const roundedAmount = roundToCurrencyPrecision(
        totalAmount,
        currency || 'USD'
      );

      // Only update if the amount actually changed (prevent unnecessary updates)
      if (
        MultiPayerCalculationService.hasAmountChanged(
          payers[0].amount,
          roundedAmount
        )
      ) {
        const updatedPayers = [
          { userId: payers[0].userId, amount: roundedAmount },
        ];

        setPayers(updatedPayers);
        lastProcessedAmountRef.current = totalAmount;

        // Use debounced onChange to prevent race conditions
        debouncedOnChange(updatedPayers);
      }
    }

    // Update the last processed amount even if we didn't update payers
    lastProcessedAmountRef.current = totalAmount;
    // Note: payers is intentionally omitted to prevent infinite loops since we update payers in this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAmount, currency, debouncedOnChange]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Track the previous error to detect transitions
  const prevErrorRef = useRef<string | undefined>(undefined);
  const hasFocusedForErrorRef = useRef(false);

  // Focus on first incomplete payer field when error is present
  const focusFirstIncomplete = useCallback(() => {
    // First, check for payers without a userId selected
    const noUserIndex = payers.findIndex(p => !p.userId);

    if (noUserIndex !== -1) {
      const selectRef = payerSelectRefs.current.get(noUserIndex);
      if (selectRef) {
        selectRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
        selectRef.focus();
        return true;
      }
    }

    // Then, check for payers with userId but no amount
    const noAmountIndex = payers.findIndex(
      p => p.userId && (!p.amount || p.amount === 0)
    );

    if (noAmountIndex !== -1) {
      const inputRef = amountInputRefs.current.get(noAmountIndex);
      if (inputRef) {
        inputRef.focus();
        return true;
      }
    }

    return false;
  }, [payers]);

  // When error appears or changes, focus on the problematic field
  useEffect(() => {
    // Detect when error transitions from empty/undefined to having a value
    const hadError = !!prevErrorRef.current;
    const hasError = !!error;

    // Focus when:
    // 1. Error just appeared (wasn't there before)
    // 2. Error changed to a different message
    // 3. Error is present and we haven't focused yet
    const shouldFocus =
      (hasError && !hadError) ||
      (hasError && error !== prevErrorRef.current) ||
      (hasError && !hasFocusedForErrorRef.current);

    if (shouldFocus) {
      hasFocusedForErrorRef.current = true;
      const timer = setTimeout(() => {
        focusFirstIncomplete();
      }, 200);
      prevErrorRef.current = error;
      return () => clearTimeout(timer);
    }

    // Reset the focus flag when error clears
    if (!hasError) {
      hasFocusedForErrorRef.current = false;
    }

    prevErrorRef.current = error;
  }, [error, focusFirstIncomplete]);

  // Calculate total and remaining using service
  const total = MultiPayerCalculationService.calculateTotal(
    payers,
    totalAmount,
    currency || 'USD'
  );

  const remaining = MultiPayerCalculationService.calculateRemaining(
    totalAmount,
    total
  );

  const remainingFormatted = formatAmountForCurrency(
    Math.abs(remaining),
    currency || 'USD'
  );

  const handlePayerChange = (index: number, userId: string) => {
    const newPayers = [...payers];
    newPayers[index] = { ...newPayers[index], userId };

    // If this is the only payer and they have a userId, set amount to total
    if (newPayers.length === 1) {
      const autoAmount = MultiPayerCalculationService.autoSetSinglePayerAmount(
        userId,
        totalAmount,
        currency || 'USD'
      );
      if (autoAmount !== null) {
        newPayers[0].amount = autoAmount;
      }
    }

    setPayers(newPayers);
    debouncedOnChange(newPayers);
  };

  const handleAmountChange = (index: number, value: string | undefined) => {
    const amount = value ? normalizeDecimalInput(value) : 0;
    const newPayers = [...payers];
    newPayers[index] = {
      ...newPayers[index],
      amount: isNaN(amount) ? 0 : amount,
    };
    setPayers(newPayers);
    debouncedOnChange(newPayers);
  };

  const handleAddPayer = () => {
    const newPayers = [...payers, { userId: '', amount: 0 }];
    setPayers(newPayers);
    debouncedOnChange(newPayers);
  };

  const handleRemovePayer = (index: number) => {
    const newPayers = payers.filter((_, i) => i !== index);
    setPayers(newPayers);
    debouncedOnChange(newPayers);
  };

  const handleAddAllMembers = () => {
    // Use service to distribute among all members
    const allPayers = MultiPayerCalculationService.distributeAmongAllMembers(
      validMembers,
      totalAmount,
      currency
    );

    setPayers(allPayers);
    debouncedOnChange(allPayers);
  };

  const handleSplitEvenly = () => {
    // Use service to split evenly
    const newPayers = MultiPayerCalculationService.splitEvenly(
      payers,
      totalAmount,
      currency
    );

    setPayers(newPayers);
    debouncedOnChange(newPayers);
  };

  const handleAutoComplete = () => {
    // Use service to auto-complete
    const newPayers = MultiPayerCalculationService.autoCompleteRemaining(
      payers,
      remaining,
      currency
    );

    setPayers(newPayers);
    debouncedOnChange(newPayers);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('expenses:fields.whoPaid')}{' '}
          <span className="text-error-500">*</span>
        </label>
        {payers.length > 0 && validMembers.length > 1 && (
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={handleAddAllMembers}
            disabled={disabled}
            className="text-orange-500 hover:text-orange-600 font-medium p-0 h-auto"
          >
            {t('expenses:actions.addAllMembers')}
          </Button>
        )}
      </div>

      {/* Payers list */}
      <div className="space-y-3">
        {payers.map((payer, index) => (
          <div
            key={index}
            className="grid grid-cols-1 sm:grid-cols-3 gap-4"
            data-testid={`payer-item-${payer.userId || index}`}
          >
            {/* Payer dropdown - 2/3 width on desktop (spans 2 columns) */}
            <div className="relative sm:col-span-2">
              <select
                ref={el => {
                  if (el) {
                    payerSelectRefs.current.set(index, el);
                  } else {
                    payerSelectRefs.current.delete(index);
                  }
                }}
                value={payer.userId}
                onChange={e => handlePayerChange(index, e.target.value)}
                className={`${getInputClasses()} pr-10 appearance-none`}
                disabled={disabled}
                required={index === 0}
                data-testid={`payer-select-${payer.userId || index}`}
              >
                <option value="">
                  {t('expenses:placeholders.selectPayer')}
                </option>
                {validMembers.map((member, memberIndex) => {
                  if (!member) {
                    return null;
                  }

                  // Handle both API formats using utility function
                  const memberData = extractMemberData(member, index);
                  const memberId = memberData.id;
                  const memberName = resolveUserName(memberData.name, t);

                  if (!memberId) {
                    return null;
                  }

                  // Check if this member is already selected in another payer slot
                  const isAlreadySelected =
                    MultiPayerCalculationService.isMemberAlreadySelected(
                      memberId,
                      payers,
                      index
                    );

                  // Skip if already selected (unless it's the current selection for this dropdown)
                  if (isAlreadySelected && payer.userId !== memberId) {
                    return null;
                  }

                  // Add "(You)" indicator for current user
                  const isCurrentUser = memberId === currentUserId;
                  // A native <option> cannot host the badge component, so the
                  // placeholder marker rides along as a text suffix.
                  const placeholderSuffix = member.isPlaceholder
                    ? ` · ${t('groups:placeholderMembers.badge')}`
                    : '';
                  const pendingSuffix = member.claimPendingFor
                    ? ` · ${t('groups:members.claimPendingFor', {
                        claimant: member.claimPendingFor,
                      })}`
                    : '';
                  const displayName = isCurrentUser
                    ? `${memberName} ${t('common:labels.you')}${placeholderSuffix}${pendingSuffix}`
                    : `${memberName}${placeholderSuffix}${pendingSuffix}`;

                  return (
                    <option
                      key={`member-${memberId || 'unknown'}-${memberIndex}`}
                      value={memberId || ''}
                    >
                      {displayName}
                    </option>
                  );
                })}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronDownIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </div>
            </div>

            {/* Amount input - 1/3 width on desktop (same as main expense amount) */}
            <div className="flex gap-2">
              <CurrencyInput
                ref={el => {
                  if (el) {
                    amountInputRefs.current.set(index, el);
                  } else {
                    amountInputRefs.current.delete(index);
                  }
                }}
                value={MultiPayerCalculationService.getSinglePayerDisplayAmount(
                  payer,
                  payers.length,
                  totalAmount,
                  currency || 'USD'
                )}
                onValueChange={value => handleAmountChange(index, value)}
                currency={currency || 'USD'}
                data-testid={`payer-amount-input-${payer.userId || index}`}
                placeholder="0.00"
                className={getInputClasses()}
                disabled={disabled || !payer.userId}
              />
              {payers.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => handleRemovePayer(index)}
                  disabled={disabled}
                  className="p-2 text-gray-400 dark:text-gray-500 hover:text-error-500 dark:hover:text-error-400 shrink-0"
                  data-testid={`remove-payer-${payer.userId || index}`}
                  aria-label={t('expenses:actions.removePayer')}
                  title={t('expenses:actions.removePayer')}
                >
                  <Trash2Icon className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {payers.length <= 1 && saveSpot}

      {/* Add payer button */}
      {payers.length < validMembers.length && (
        <Button
          variant="ghost"
          size="sm"
          type="button"
          onClick={handleAddPayer}
          disabled={disabled}
          leftIcon={<UserPlusIcon className="w-4 h-4" />}
          className="text-orange-500 hover:text-orange-600 font-medium"
        >
          {t('expenses:actions.addPayer')}
        </Button>
      )}

      {/* Actions for multiple payers */}
      {payers.length > 1 && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={handleSplitEvenly}
            disabled={disabled || totalAmount <= 0}
            className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            {t('expenses:actions.splitEvenly')}
          </Button>
          {remaining > 0 && (
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={handleAutoComplete}
              disabled={disabled}
              className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50"
            >
              {t('expenses:actions.autoComplete')}
            </Button>
          )}
        </div>
      )}

      {/* Summary */}
      {totalAmount > 0 && (
        <div
          className={clsx(
            'text-sm p-3 rounded-lg border',
            Math.abs(remaining) < 0.01
              ? 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800 text-success-700 dark:text-success-300'
              : 'bg-warning-50 dark:bg-warning-900/20 border-warning-200 dark:border-warning-800 text-warning-700 dark:text-warning-300'
          )}
        >
          <div className="flex items-center gap-2">
            {Math.abs(remaining) < 0.01 ? (
              <>
                <UserIcon className="w-4 h-4" />
                {payers.filter(p => p.userId && p.amount > 0).length === 1
                  ? t('expenses:messages.singlePayerReady')
                  : t('expenses:messages.multiplePayersReady')}
              </>
            ) : (
              <>
                <AlertCircleIcon className="w-4 h-4" />
                {remaining > 0 ? (
                  <>
                    {t('expenses:messages.stillNeedToAllocate')}{' '}
                    <span className="font-medium">{remainingFormatted}</span>
                  </>
                ) : (
                  <>
                    {t('expenses:messages.overAllocated')}{' '}
                    <span className="font-medium">{remainingFormatted}</span>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="text-sm text-error-600 dark:text-error-400 mt-1">
          {error}
        </div>
      )}
    </div>
  );
}

export default MultiPayerSelect;
